import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import Header from '../header/header';
import Footer from '../footer/footer';

import 'aos/dist/aos.css';
import '@styles/main.scss';

import { registerUrl, warmupBrandedRegisterUrl, dmarcBrandedRegisterUrl, emailFinderBrandedRegisterUrl } from '../../i18n/environments';
import detectBrowser from '../../utils/detectBrowser';
import SmallHeader from '../header/small-header';
import CustomFooter from '../footer/custom-footer';
import CookiePreferencesComponent from '../accept-cookies';
import { CookieContextType } from './interface';
import { getCookiePreferences } from '../accept-cookies';
import HubSpotTracking from '../hubspotTracking/hubspotTracking';
import GoogleOktaSSO from '../googke-okta-sso/GoogleOktaSSO';

type LayoutProps = {
  isAltHeader?: boolean;
  isHomepage?: boolean;
  isSmallNav?: boolean;
  hasLanguageFooter?: boolean;
  children: ReactNode;
  onlyLanguage?: boolean;
  customNavElements?: Array<string>;
  hasPromo?: string;
  customPricingLink?: string;
  registerUrlBrandDestination?: string;
  stickyAB?: {
    translations: string[];
    pageUrl: string;
  };
};

let AOS;

const defaultCookieContextValue: CookieContextType = {
  isCookieModalVisible: false,
  showSettingsInCookieModal: false,
  hideBackButton: false,
  toggleHideBackButton: () => {},
  // hideBackButton: false,
  // toggleHideBackButton: () => {},
  toggleCookieModal: () => {},
  toggleCookieSettings: () => {},
};

export const CookieState = createContext<CookieContextType>(defaultCookieContextValue);

const Layout: React.FC<LayoutProps> = ({
  isAltHeader,
  isHomepage,
  isSmallNav,
  hasLanguageFooter,
  children,
  onlyLanguage,
  customNavElements,
  hasPromo,
  customPricingLink,
  registerUrlBrandDestination = '',
  stickyAB = {
    translations: [],
    pageUrl: '',
  },
}) => {
  const [isEbookPage, setIsEbookPage] = useState(false);
  const [isHolidayCheckPage, setIsHolidayCheckPage] = useState(false);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isCookieModalVisible, setIsCookieModalVisible] = useState<boolean>(false);
  const [showSettingsInCookieModal, setShowSettingsInCookieModal] = useState<boolean>(false);
  const [hideBackButton, setHideBackButton] = useState<boolean>(false);

  const providerValue = {
    hideBackButton,
    isCookieModalVisible,
    showSettingsInCookieModal,
    toggleHideBackButton: () => {
      setHideBackButton(true);
    },
    toggleCookieModal: () => {
      setIsCookieModalVisible(!isCookieModalVisible);
    },
    toggleCookieSettings: (pref: boolean) => {
      setShowSettingsInCookieModal(pref ?? !showSettingsInCookieModal);
      if (pref) {
        setHideBackButton(true);
      }
    },
  };

  const registerUrlDestination =
    registerUrlBrandDestination === 'warmup'
      ? warmupBrandedRegisterUrl
      : registerUrlBrandDestination === 'dmarc'
        ? dmarcBrandedRegisterUrl
        : registerUrlBrandDestination === 'emailFinder'
          ? emailFinderBrandedRegisterUrl
          : registerUrl;

  useEffect(() => {
    const isEbookPage = window.location.href.indexOf('integrations') === -1 && window.location.href.indexOf('ebook') !== -1;
    setIsEbookPage(isEbookPage);
    const isHolidayCheckPage = window.location.href.indexOf('holiday-checklist') !== -1;
    setIsHolidayCheckPage(isHolidayCheckPage);
  }, []);

  if (typeof window !== 'undefined' && window && window.NodeList && !NodeList.prototype.forEach) {
    (NodeList.prototype as any).forEach = Array.prototype.forEach;
  }

  useEffect(() => {
    const safariOlderBrowsers = ['Safari 7', 'Safari 8', 'Safari 9'];
    const AOS = require('aos');
    if (!safariOlderBrowsers.includes(detectBrowser!)) {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
      });
    } else {
      AOS.init({
        disable: true,
      });
    }
  }, []);

  useEffect(() => {
    if (AOS) {
      AOS.refresh();
    }
  });

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 1024);
  };

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const preferences = getCookiePreferences();
  const hasGivenConsent = preferences?.necessary;

  return (
    <div className="wrapper" style={{ overflow: isEbookPage || isHolidayCheckPage ? 'initial' : 'hidden' }}>
      {hasGivenConsent && (
        <>
          <Helmet>
            {!isMobile && (
              <script
                id="ze-snippet"
                src="https://static.zdassets.com/ekr/snippet.js?key=33291a34-2b07-4b2d-b445-d061c553d19e"
                async
              ></script>
            )}
          </Helmet>
          <HubSpotTracking />
        </>
      )}
      {isSmallNav ? (
        <SmallHeader hasPromo={hasPromo} registerUrlDestination={registerUrlDestination} stickyAB={stickyAB} />
      ) : (
        <Header
          isAlt={isAltHeader}
          isHomepage={isHomepage}
          customNavElements={customNavElements}
          hasPromo={hasPromo}
          hideNav={undefined}
          customPricingLink={customPricingLink}
          registerUrlDestination={registerUrlDestination}
          stickyAB={stickyAB}
        />
      )}

      <main id="main" className="main" role="main" tabIndex={-1}>
        {children}
      </main>
      <CookieState.Provider value={providerValue}>
        {!hasLanguageFooter && <Footer isSmallFooter={isSmallNav} />}
        {hasLanguageFooter && <CustomFooter onlyLanguage={onlyLanguage} />}

        <CookiePreferencesComponent />
      </CookieState.Provider>
      <GoogleOktaSSO />
    </div>
  );
};

export default Layout;
